import { CircleProgress } from '@cdab/components'
import {
  Column,
  DealerInfoBlock,
  FavoriteAuditIconButton,
  Row
} from '@cdab/scania/qpr/components/atoms'
import { AuditBadge } from '@cdab/scania/qpr/components/molecules'
import type {
  AuditModel,
  DealerModel,
  FavoriteAuditModel
} from '@cdab/scania/qpr/offline/models'
import { TdsBlock } from '@scania/tegel-react'
import { observer } from 'mobx-react-lite'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { AuditEditModal } from '../audit-modal/audit-edit-modal'
import { ActionsBlocks } from './actions-block'
import { AuditAttachments } from './attachments'
import { AuditInfo } from './audit-info'
import {
  AuditBadges,
  CircleProgressLabel,
  CircleProgressWrapper,
  Numbers
} from './audit-summary.styles'
import { AuditNotes } from './notes'

export const AuditSummary = observer(
  (props: {
    audit: AuditModel
    dealer: DealerModel
    favoriteAuditData?: FavoriteAuditModel
  }) => {
    const { t } = useTranslation()
    const modalRef = useRef<HTMLTdsModalElement>(null)

    const { audit, dealer, favoriteAuditData } = props

    const mandatoryAuditPoints = audit.auditPoints.filter(ap => ap.isMandatory)
    const completedMandatoryAuditPoints = mandatoryAuditPoints.filter(
      auditPoint => auditPoint.isMandatory && auditPoint.score === true
    ).length

    const developmentAuditPoints = audit.auditPoints.filter(
      auditPoint => !auditPoint.isMandatory
    )
    const approvedDevelopmentAuditPoints = developmentAuditPoints.filter(
      auditPoint => auditPoint.score === true
    ).length

    const openEditAuditModal = () => {
      modalRef.current?.showModal()
    }

    const closeEditAuditModal = () => {
      modalRef.current?.closeModal()
    }

    return (
      <>
        <Row className='tds-hide-lg'>
          <Column width={12}>
            <AuditBadges>
              <FavoriteAuditIconButton
                favoriteAudit={favoriteAuditData}
                auditId={audit.id}
              />
              {audit.isCertified && <AuditBadge type='certify' />}
              {audit.isReadonly && <AuditBadge type='readonly' />}
            </AuditBadges>
          </Column>
        </Row>

        <Row>
          {/* Main content block*/}
          <Column width={12} lg={8} className='tds-u-pb1'>
            <h4 className='tds-u-mt0'>{t('statistics')}</h4>
            <TdsBlock modeVariant='secondary'>
              <CircleProgressWrapper>
                <CircleProgress
                  percentage={
                    completedMandatoryAuditPoints /
                    (mandatoryAuditPoints.length || 1)
                  }
                  label={
                    <CircleProgressLabel>
                      {t('completed-audit-points')} (
                      <Numbers>
                        {completedMandatoryAuditPoints}/
                        {mandatoryAuditPoints.length}
                      </Numbers>
                      )
                    </CircleProgressLabel>
                  }
                />
                <CircleProgress
                  percentage={
                    approvedDevelopmentAuditPoints /
                    (developmentAuditPoints.length || 1)
                  }
                  label={
                    <CircleProgressLabel>
                      {t('approved-development-points')} (
                      <Numbers>
                        {approvedDevelopmentAuditPoints}/
                        {developmentAuditPoints.length || 1}
                      </Numbers>
                      )
                    </CircleProgressLabel>
                  }
                />
              </CircleProgressWrapper>
            </TdsBlock>

            {/* in xs-lg show dealer-info here, otherwise put it in the aside */}
            <DealerInfoBlock
              dealer={dealer}
              className='tds-show-xs tds-hide-lg'
            />
            <AuditInfo
              audit={audit}
              className='tds-show-xs tds-hide-lg'
              onOpenAuditEditModal={openEditAuditModal}
            />
            <div className='tds-u-mt3'>
              <AuditNotes audit={audit} />
            </div>
          </Column>

          {/* side content block */}
          <Column width={12} lg={4} className='tds-u-pb1'>
            <DealerInfoBlock
              dealer={dealer}
              className='tds-hide-xs tds-show-lg'
            />
            <AuditInfo
              audit={audit}
              className='tds-hide-xs tds-show-lg'
              onOpenAuditEditModal={openEditAuditModal}
            />
            <AuditAttachments audit={audit} />
            <ActionsBlocks audit={audit} />
          </Column>
        </Row>
        <AuditEditModal
          audit={audit}
          ref={modalRef}
          onClose={closeEditAuditModal}
        />
      </>
    )
  }
)
